import type { QueryClient } from "@tanstack/react-query";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { HTTPError } from "ky";
import { useEffect } from "react";
import { api } from "@/api/client";
import type { User } from "@/api/types";
import { toast } from "@/components/ui/toast";
import { consumeAuthPopup } from "@/lib/federated-auth";
import { assertCurrentSession, sessionRevision } from "@/lib/session-request-scope";
import { transitionSession } from "@/lib/session-transition";
import { useAuthUIStore } from "@/stores/auth-ui";

interface AuthCompleteMessage {
  type: "auth_complete";
  status: "success" | "error";
  intent?: "sign_in" | "link";
  message?: string;
}

function isAuthCompleteMessage(data: unknown): data is AuthCompleteMessage {
  return (
    typeof data === "object" && data !== null && "type" in data && data.type === "auth_complete"
  );
}

export function useAuthCallback(queryClient: QueryClient) {
  const navigate = useNavigate();
  const router = useRouter();
  const redirectAfterLogin = useAuthUIStore((s) => s.redirectAfterLogin);
  const setRedirect = useAuthUIStore((s) => s.setRedirect);

  useEffect(() => {
    const handler = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!isAuthCompleteMessage(event.data)) return;
      const popup = consumeAuthPopup(event);
      if (!popup) return;
      const expectedRevision = sessionRevision();

      if (popup.revision !== expectedRevision) {
        if (event.data.status !== "success") return;
        // A known popup may already have changed the shared cookie before
        // its stale completion arrives. Discard private state and confirm
        // that cookie without replaying the previous account's intent.
        transitionSession(queryClient);
        const reconciliationRevision = sessionRevision();
        try {
          await queryClient.fetchQuery({
            queryKey: ["me"],
            queryFn: async (): Promise<User | null> => {
              try {
                return await api.get("me").json<User>();
              } catch (error) {
                if (error instanceof HTTPError && error.response.status === 401) return null;
                throw error;
              }
            },
            retry: false,
          });
        } catch {
          if (sessionRevision() !== reconciliationRevision) return;
          toast({
            title: "Unable to confirm your current account",
            description: "Please refresh the page to try again.",
            variant: "error",
          });
        }
        if (sessionRevision() === reconciliationRevision) await router.invalidate();
        return;
      }

      if (event.data.status === "success") {
        try {
          const user = await api.get("me").json<User>();
          assertCurrentSession(expectedRevision);
          transitionSession(queryClient, user);
          if (popup.intent === "link") {
            toast({ title: "Account connected", variant: "success" });
          } else {
            const destination = redirectAfterLogin || "/";
            setRedirect(null);
            await navigate({ to: destination });
          }
        } catch {
          if (sessionRevision() !== expectedRevision) return;
          toast({
            title: "Unable to confirm sign-in",
            description: "Please try again.",
            variant: "error",
          });
        }
      } else if (event.data.status === "error") {
        toast({
          title: "Sign-in failed",
          description: event.data.message || "Something went wrong. Please try again.",
          variant: "error",
        });
      }
    };

    const listener = (event: MessageEvent) => {
      void handler(event);
    };
    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, [queryClient, router, navigate, redirectAfterLogin, setRedirect]);
}
